import {
  ArrayHelperProjector,
  ArrayHelperSelectorType,
  GeneratedSelectorCreatorsFor,
  GeneratedSelectorsFor,
  LoadStatusSelectors,
  LoadStatusSubstate,
  MinimalPersonNameSelectorCreators,
  MinimalPersonNameSelectors,
  MxSelector,
  MxSelectorCreator,
  MxSelectorCreatorBaseObjectType,
  MxSelectorCreatorReturnType,
  NumberHelperProjector,
  NumberHelperSelectorType,
  PersonNameSelectorCreators,
  PersonNameSelectorName,
  PersonNameSelectors,
  PersonSelectorCreatorName,
  PersonSelectorName
} from './store.types';
import {
  abbreviatedNameFrom,
  abbreviatedPreferredNameFrom,
  fullNameFrom,
  fullNameWithPronounsFrom,
  fullPreferredNameFrom,
  fullPreferredNameWithPronounsFrom,
  lastNameWithSuffixFrom,
  preferredOrFirstNameFrom
} from './common/person.helpers';
import {LoadStatus} from './common/common.types';
import {createSelector} from '@ngrx/store';
import {lowerCamelCaseToUpperCamelCase} from './transformation.helpers';
import {ValueFrom} from './utility-type.helpers';
import {isPopulatedString, isString, isUndefined} from '@aviary/store/common/typing.helpers';

export function generateSelectorsDependentOn<ObjectType extends object, NamePrefix extends string>(
  dependencySelector: MxSelector<ObjectType>, fallbackObject: ObjectType, namePrefix: NamePrefix
): GeneratedSelectorsFor<ObjectType, NamePrefix> {
  return Object.entries(fallbackObject).reduce(
    (accumulator, [fallbackPropertyName, fallbackValue]: [string, ValueFrom<ObjectType>]) => {
      const selectorName = namePrefix ? `${namePrefix}${lowerCamelCaseToUpperCamelCase(fallbackPropertyName)}` : fallbackPropertyName;

      const selector: MxSelector<ValueFrom<ObjectType>> =
        createSelector(
          dependencySelector,
          parentObject => parentObject[fallbackPropertyName] ?? fallbackValue
        );

      return {
        ...accumulator,
        [selectorName]: selector
      };
    },
    {} as GeneratedSelectorsFor<ObjectType, NamePrefix>
  );
}

export function generateNumberHelperSelectorDependentOn(numberSelector: MxSelector<number>, type: NumberHelperSelectorType): MxSelector<boolean> {
  return createSelector(
    numberSelector,
    generateNumberHelperProjectorFor(type)
  );
}

function generateNumberHelperProjectorFor(type: NumberHelperSelectorType): NumberHelperProjector {
  switch (type) {
    case 'IS_NEGATIVE':
      return number => number < 0;

    case 'IS_ZERO':
      return number => number === 0

    case 'IS_POSITIVE':
      return number => number > 0
  }
}

export function generateArrayHelperSelectorDependentOn(arraySelector: MxSelector<unknown[]>, type: ArrayHelperSelectorType): MxSelector<boolean> {
  return createSelector(
    arraySelector,
    generateArrayHelperProjectorFor(type)
  );
}

function generateArrayHelperProjectorFor(type: ArrayHelperSelectorType): ArrayHelperProjector {
  switch (type) {
    case 'IS_EMPTY':
      return array => array.length === 0;

    case 'IS_POPULATED':
      return array => array.length > 0;
  }
}

export function generateIndependentSelectorCreatorsFor<ObjectType extends object, NamePrefix extends string>(
  fallbackObject: ObjectType, namePrefix: NamePrefix
): GeneratedSelectorCreatorsFor<ObjectType, NamePrefix, ObjectType> {
  return Object.entries(fallbackObject).reduce(
    (accumulator, [fallbackPropertyName, fallbackValue]: [string, ValueFrom<ObjectType>]) => {
      const selectorCreatorName = namePrefix ? `${namePrefix}${lowerCamelCaseToUpperCamelCase(fallbackPropertyName)}For` : `${fallbackPropertyName}For`;

      const selectorCreator: MxSelectorCreator<[ObjectType], ValueFrom<ObjectType>> =
        parentObject => createSelector(
          () => parentObject?.[fallbackPropertyName] ?? fallbackValue
        );

      return {
        ...accumulator,
        [selectorCreatorName]: selectorCreator
      };
    },
    {} as GeneratedSelectorCreatorsFor<ObjectType, NamePrefix, ObjectType>
  );
}

export function generateSelectorCreatorsDependentOn<
  DependencySelectorCreator extends MxSelectorCreator<[any], any>, // eslint-disable-line @typescript-eslint/no-explicit-any -- These are literally "any"!
  ObjectType extends object,
  NamePrefix extends string
>(
  dependencySelectorCreator: DependencySelectorCreator, fallbackObject: ObjectType, namePrefix: NamePrefix
): GeneratedSelectorCreatorsFor<ObjectType, NamePrefix, MxSelectorCreatorBaseObjectType<DependencySelectorCreator>> {
  type BaseObjectType = MxSelectorCreatorBaseObjectType<typeof dependencySelectorCreator>;
  type ReturnType = MxSelectorCreatorReturnType<typeof dependencySelectorCreator>;

  return Object.entries(fallbackObject).reduce(
    (accumulator, [key, fallbackValue]: [string, ValueFrom<ObjectType>]) => {
      const selectorCreatorName = namePrefix ? `${namePrefix}${lowerCamelCaseToUpperCamelCase(key)}For` : `${key}For`;

      const selectorCreator: MxSelectorCreator<[BaseObjectType], ReturnType> =
        parentObject => createSelector(
          dependencySelectorCreator(parentObject),
          dependencyValue => dependencyValue[key] ?? fallbackValue
        );

      return {
        ...accumulator,
        [selectorCreatorName]: selectorCreator
      };
    },
    {} as GeneratedSelectorCreatorsFor<ObjectType, NamePrefix, BaseObjectType>
  );
}

export function generateNumberHelperSelectorCreatorDependentOn<ObjectType extends object>(
  numberSelectorCreator: MxSelectorCreator<[ObjectType], number>, type: NumberHelperSelectorType
): MxSelectorCreator<[ObjectType], boolean> {
  return object => createSelector(
    numberSelectorCreator(object),
    generateNumberHelperProjectorFor(type)
  );
}

export function generateArrayHelperSelectorCreatorDependentOn<ObjectType extends object>(
  arraySelectorCreator: MxSelectorCreator<[ObjectType], unknown[]>, type: ArrayHelperSelectorType
): MxSelectorCreator<[ObjectType], boolean> {
  return object => createSelector(
    arraySelectorCreator(object),
    generateArrayHelperProjectorFor(type)
  );
}

export function generateLoadStatusSelectorsFor<T extends LoadStatusSubstate>(substateSelector: MxSelector<T>): LoadStatusSelectors {
  const loadStatus: MxSelector<LoadStatus> = createSelector(
    substateSelector,
    ({loadStatus}) => loadStatus
  );

  return {
    loadStatus,

    preload: createSelector(
      loadStatus,
      status => status === 'PRELOAD'
    ),

    loading: createSelector(
      loadStatus,
      status => status === 'LOADING'
    ),

    awaitingFirstLoad: createSelector(
      loadStatus,
      status => ['PRELOAD', 'LOADING'].includes(status)
    ),

    reloading: createSelector(
      loadStatus,
      status => status === 'RELOADING'
    ),

    awaitingAnyLoad: createSelector(
      loadStatus,
      status => ['PRELOAD', 'LOADING', 'RELOADING'].includes(status)
    ),

    loaded: createSelector(
      loadStatus,
      status => status === 'LOADED'
    ),

    loadFailed: createSelector(
      loadStatus,
      status => status === 'FAILED'
    ),

    finishedLoading: createSelector(
      loadStatus,
      status => ['LOADED', 'FAILED'].includes(status)
    )
  };
}

// Override signature for minimal person name selectors
export function generatePersonNameSelectorsFor<NamePrefix extends string = ''>(
  firstName: MxSelector<string>,
  middleName: MxSelector<string>,
  lastName: MxSelector<string>,
  suffix: MxSelector<string>,
  selectorNamePrefix: NamePrefix
): MinimalPersonNameSelectors<NamePrefix>;

// Override signature for all person name selectors
export function generatePersonNameSelectorsFor<NamePrefix extends string = ''>(
  firstName: MxSelector<string>,
  middleName: MxSelector<string>,
  lastName: MxSelector<string>,
  suffix: MxSelector<string>,
  preferredName: MxSelector<string>,
  pronouns: MxSelector<string>,
  selectorNamePrefix: NamePrefix
): PersonNameSelectors<NamePrefix>;

// Implementation for both person name selector generator overrides
export function generatePersonNameSelectorsFor<NamePrefix extends string = ''>(
  firstName: MxSelector<string>,
  middleName: MxSelector<string>,
  lastName: MxSelector<string>,
  suffix: MxSelector<string>,
  selectorNamePrefixOrPreferredName: NamePrefix | MxSelector<string>,
  possiblePronouns?: MxSelector<string>,
  possibleSelectorNamePrefix?: NamePrefix
): MinimalPersonNameSelectors<NamePrefix> | PersonNameSelectors<NamePrefix> {
  const minimalSelectorsRequested: boolean = isString(selectorNamePrefixOrPreferredName);
  const prefix: NamePrefix = (minimalSelectorsRequested ? selectorNamePrefixOrPreferredName : possibleSelectorNamePrefix) as NamePrefix;

  const minimalSelectors: MinimalPersonNameSelectors<NamePrefix> = {
    [buildPersonNameSelectorNameFrom(prefix, 'lastNameWithSuffix')]:
      createSelector(lastName, suffix, lastNameWithSuffixFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'fullName')]:
      createSelector(firstName, middleName, lastName, suffix, fullNameFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'abbreviatedName')]:
      createSelector(firstName, lastName, suffix, abbreviatedNameFrom),
  } as MinimalPersonNameSelectors<NamePrefix>;

  if (minimalSelectorsRequested) return minimalSelectors;

  const preferredName: MxSelector<string> = selectorNamePrefixOrPreferredName as MxSelector<string>;
  const pronouns: MxSelector<string> = possiblePronouns as MxSelector<string>;

  return {
    ...minimalSelectors,
    [buildPersonNameSelectorNameFrom(prefix, 'preferredOrFirstName')]:
      createSelector(preferredName, firstName, preferredOrFirstNameFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'fullPreferredName')]:
      createSelector(firstName, middleName, lastName, suffix, preferredName, fullPreferredNameFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'abbreviatedPreferredName')]:
      createSelector(firstName, lastName, suffix, preferredName, abbreviatedPreferredNameFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'fullNameWithPronouns')]:
      createSelector(firstName, middleName, lastName, suffix, pronouns, fullNameWithPronounsFrom),
    [buildPersonNameSelectorNameFrom(prefix, 'fullPreferredNameWithPronouns')]:
      createSelector(firstName, middleName, lastName, suffix, preferredName, pronouns, fullPreferredNameWithPronounsFrom)
  } as PersonNameSelectors<NamePrefix>;
}

// Override signature for minimal person name selector creators
export function generatePersonNameSelectorCreatorsFor<ObjectType, NamePrefix extends string = ''>(
  firstNameFor: MxSelectorCreator<[ObjectType], string>,
  middleNameFor: MxSelectorCreator<[ObjectType], string>,
  lastNameFor: MxSelectorCreator<[ObjectType], string>,
  suffixFor: MxSelectorCreator<[ObjectType], string>,
  creatorNamePrefix: NamePrefix
): MinimalPersonNameSelectorCreators<ObjectType, NamePrefix>;

// Override signature for all person name selector creators
export function generatePersonNameSelectorCreatorsFor<ObjectType, NamePrefix extends string = ''>(
  firstNameFor: MxSelectorCreator<[ObjectType], string>,
  middleNameFor: MxSelectorCreator<[ObjectType], string>,
  lastNameFor: MxSelectorCreator<[ObjectType], string>,
  suffixFor: MxSelectorCreator<[ObjectType], string>,
  preferredNameFor: MxSelectorCreator<[ObjectType], string>,
  pronounsFor: MxSelectorCreator<[ObjectType], string>,
  creatorNamePrefix: NamePrefix
): PersonNameSelectorCreators<ObjectType, NamePrefix>;

// Implementation for both person name selector creator generator overrides
export function generatePersonNameSelectorCreatorsFor<ObjectType, NamePrefix extends string = ''>(
  firstNameFor: MxSelectorCreator<[ObjectType], string>,
  middleNameFor: MxSelectorCreator<[ObjectType], string>,
  lastNameFor: MxSelectorCreator<[ObjectType], string>,
  suffixFor: MxSelectorCreator<[ObjectType], string>,
  creatorNamePrefixOrPreferredNameFor: NamePrefix | MxSelectorCreator<[ObjectType], string>,
  possiblePronounsFor?: MxSelectorCreator<[ObjectType], string>,
  possibleCreatorNamePrefix?: NamePrefix
): MinimalPersonNameSelectorCreators<ObjectType, NamePrefix> | PersonNameSelectorCreators<ObjectType, NamePrefix> {
  const minimalCreatorsRequested: boolean = isString(creatorNamePrefixOrPreferredNameFor);
  const prefix: NamePrefix = (minimalCreatorsRequested ? creatorNamePrefixOrPreferredNameFor : possibleCreatorNamePrefix) as NamePrefix;

  const minimalCreators: MinimalPersonNameSelectorCreators<ObjectType, NamePrefix> = {
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'lastNameWithSuffix')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(lastNameFor(object), suffixFor(object), lastNameWithSuffixFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'fullName')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(firstNameFor(object), middleNameFor(object), lastNameFor(object), suffixFor(object), fullNameFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'abbreviatedName')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(firstNameFor(object), lastNameFor(object), suffixFor(object), abbreviatedNameFrom),
        fallback
      )
  } as MinimalPersonNameSelectorCreators<ObjectType, NamePrefix>;

  if (minimalCreatorsRequested) return minimalCreators;

  const preferredNameFor: MxSelectorCreator<[ObjectType], string> = creatorNamePrefixOrPreferredNameFor as MxSelectorCreator<[ObjectType], string>;
  const pronounsFor: MxSelectorCreator<[ObjectType], string> = possiblePronounsFor as MxSelectorCreator<[ObjectType], string>;

  return {
    ...minimalCreators,
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'preferredOrFirstName')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(preferredNameFor(object), firstNameFor(object), preferredOrFirstNameFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'fullPreferredName')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(firstNameFor(object), middleNameFor(object), lastNameFor(object), suffixFor(object), preferredNameFor(object), fullPreferredNameFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'abbreviatedPreferredName')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(firstNameFor(object), lastNameFor(object), suffixFor(object), preferredNameFor(object), abbreviatedPreferredNameFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'fullNameWithPronouns')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(firstNameFor(object), middleNameFor(object), lastNameFor(object), suffixFor(object), pronounsFor(object), fullNameWithPronounsFrom),
        fallback
      ),
    [buildPersonNameSelectorCreatorNameFrom(prefix, 'fullPreferredNameWithPronouns')]:
      (object: ObjectType, fallback?: string) => asSelectorWithPossibleFallback(
        createSelector(
          firstNameFor(object), middleNameFor(object), lastNameFor(object), suffixFor(object), preferredNameFor(object), pronounsFor(object), fullPreferredNameWithPronounsFrom
        ),
        fallback
      )
  } as PersonNameSelectorCreators<ObjectType, NamePrefix>;
}

function buildPersonNameSelectorNameFrom<NamePrefix extends string, SelectorName extends PersonNameSelectorName>(
  prefix: NamePrefix | undefined, selectorName: SelectorName
): PersonSelectorName<NamePrefix, SelectorName> {
  return asPrefixedString(prefix, selectorName) as PersonSelectorName<NamePrefix, SelectorName>;
}

function buildPersonNameSelectorCreatorNameFrom<NamePrefix extends string, SelectorName extends PersonNameSelectorName>(
  prefix: NamePrefix, selectorName: SelectorName
): PersonSelectorCreatorName<NamePrefix, SelectorName> {
  return asPrefixedString(prefix, `${selectorName}For`) as PersonSelectorCreatorName<NamePrefix, SelectorName>;
}

function asPrefixedString(prefix: string | undefined, string: string): string {
  return isPopulatedString(prefix) ? `${prefix}${lowerCamelCaseToUpperCamelCase(string)}` : string;
}

function asSelectorWithPossibleFallback(selector: MxSelector<string>, fallback?: string): MxSelector<string> {
  if (isUndefined(fallback)) return selector;

  return createSelector(
    selector,
    selectorValue => isPopulatedString(selectorValue) ? selectorValue : fallback
  );
}
